import { useOrder, useSettings } from '@restaurant/api-client/react';
import { buildReceipt, DEFAULT_SETTINGS, formatMoney, isEditable, ORDER_STATUS_LABEL, type OrderDto } from '@restaurant/shared';
import { format } from 'date-fns';
import { FileText, MapPin, MessageSquare, Pencil, Printer, Send, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Skeleton, Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { formatPhone } from '@/lib/utils';
import { OrderActions } from './OrderActions';
import { OrderEditor, toEditorValue } from './OrderEditor';
import { STATUS_STYLE } from './format';
import { printReceipt } from './printReceipt';
import { printTicket } from './printTicket';

export function OrderDrawer({ orderId, onClose }: { orderId: string | null; onClose: () => void }) {
  return (
    <Dialog open={!!orderId} onOpenChange={(o) => !o && onClose()}>
      {orderId ? <OrderDetail orderId={orderId} /> : null}
    </Dialog>
  );
}

function OrderDetail({ orderId }: { orderId: string }) {
  const { data: o } = useOrder(orderId);
  const { data: settings } = useSettings();
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState(false);
  const currency = settings?.currency ?? 'LKR';
  if (!o) return <DialogContent title="Order" side="right"><Skeleton className="h-40" /></DialogContent>;

  const hasReceipt = o.status !== 'draft' && o.status !== 'cancelled';
  const sendReceipt = async () => {
    setSending(true);
    try {
      const r = await api.orders.sendReceipt(o.id);
      toast.success(`${r.filename} sent on WhatsApp`);
    } catch (e) {
      toastError(e);
    } finally {
      setSending(false);
    }
  };

  return (
    <DialogContent title={`Order #${o.number}`} description={`${o.type === 'delivery' ? 'Delivery' : 'Pickup'} · created ${format(new Date(o.createdAt), 'dd MMM HH:mm')}`} side="right">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={STATUS_STYLE[o.status].variant}>{ORDER_STATUS_LABEL[o.status]}</Badge>
        {o.source === 'ai' ? <Badge variant="outline"><Sparkles className="size-3" /> AI draft</Badge> : null}
        <span className="text-sm text-muted-foreground">{o.paymentMethod === 'cash' ? 'Cash' : 'Card at pickup'}</span>
      </div>

      {editing ? (
        <OrderEditor
          initial={toEditorValue(o)}
          submitLabel="Save changes"
          secondary={<Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>}
          onSubmit={async (v) => {
            try {
              await api.orders.update(o.id, { ...v, address: v.type === 'delivery' ? v.address : null, notes: v.notes || null });
              toast.success('Order updated');
              setEditing(false);
            } catch (err) {
              toastError(err);
            }
          }}
        />
      ) : (
        <>
          <OrderActions order={o} size="default" />
          {o.aiUnclear.length ? (
            <div className="rounded-lg border border-warning/50 bg-warning/10 p-3 text-sm">
              <p className="mb-1 font-medium">Check with the customer</p>
              <ul className="list-disc pl-5">{o.aiUnclear.map((u, i) => <li key={i}>{u}</li>)}</ul>
            </div>
          ) : null}
          <div className="rounded-lg border">
            {o.items.map((i) => (
              <div key={i.id} className="flex justify-between gap-3 border-b px-3 py-2 text-sm last:border-0">
                <div>
                  <p><span className="font-medium tabular-nums">{i.quantity}×</span> {i.name}</p>
                  {i.options.length ? <p className="text-xs text-muted-foreground">{i.options.map((x) => x.name).join(', ')}</p> : null}
                  {i.notes ? <p className="text-xs italic text-muted-foreground">“{i.notes}”</p> : null}
                </div>
                <span className="tabular-nums">{formatMoney(i.lineTotalCents, currency)}</span>
              </div>
            ))}
            <div className="space-y-0.5 bg-muted/50 px-3 py-2 text-sm">
              {o.deliveryFeeCents ? <div className="flex justify-between text-muted-foreground"><span>Delivery</span><span>{formatMoney(o.deliveryFeeCents, currency)}</span></div> : null}
              <div className="flex justify-between font-semibold"><span>Total</span><span className="tabular-nums">{formatMoney(o.totalCents, currency)}</span></div>
            </div>
          </div>
          <div className="grid gap-2 text-sm">
            <p><span className="text-muted-foreground">Customer:</span> {o.customer.name ?? '—'} · {formatPhone(o.customer.waId)}</p>
            {o.address ? <p className="flex gap-1"><MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" /> {o.address}</p> : null}
            {o.location ? (
              <a className="text-primary hover:underline" target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${o.location.latitude},${o.location.longitude}`}>Open location in Maps</a>
            ) : null}
            {o.notes ? <p><span className="text-muted-foreground">Note:</span> {o.notes}</p> : null}
            {o.cancelReason ? <p className="text-destructive">Cancelled: {o.cancelReason}</p> : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {isEditable(o.status) ? <Button variant="outline" size="sm" onClick={() => setEditing(true)}><Pencil /> Edit items</Button> : null}
            <Button variant="outline" size="sm" onClick={() => printTicket(o, settings?.restaurantName ?? '', currency)}><Printer /> Print ticket</Button>
            {hasReceipt ? (
              <>
                <Button variant="outline" size="sm" onClick={() => printReceipt(buildReceipt(o, settings ?? DEFAULT_SETTINGS))}><FileText /> Print receipt</Button>
                {o.conversationId ? (
                  <Button variant="outline" size="sm" disabled={sending} onClick={() => void sendReceipt()}>
                    {sending ? <Spinner /> : <Send />} Send receipt
                  </Button>
                ) : null}
              </>
            ) : null}
            {o.conversationId ? (
              <Link to={`/inbox/${o.conversationId}`} className="inline-flex h-8 items-center gap-2 rounded-md border bg-card px-3 text-xs font-medium hover:bg-accent">
                <MessageSquare className="size-4" /> Open chat
              </Link>
            ) : null}
          </div>
          <Timeline order={o} />
        </>
      )}
    </DialogContent>
  );
}

function Timeline({ order }: { order: OrderDto }) {
  if (!order.events?.length) return null;
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Timeline</p>
      <ol className="space-y-2 border-l pl-4">
        {order.events.map((e) => {
          const note = e.type === 'notification' ? notificationText(e.data) : e.type === 'status' ? `${ORDER_STATUS_LABEL[e.fromStatus!]} → ${ORDER_STATUS_LABEL[e.toStatus!]}` : e.type === 'created' ? `Created as ${ORDER_STATUS_LABEL[e.toStatus!]}` : 'Items updated';
          return (
            <li key={e.id} className="relative text-sm">
              <span className="absolute -left-[21px] top-1.5 size-2 rounded-full bg-border" />
              <p>{note}{(e.data as { reason?: string } | null)?.reason ? ` · ${(e.data as { reason: string }).reason}` : ''}</p>
              <p className="text-xs text-muted-foreground">{format(new Date(e.createdAt), 'dd MMM HH:mm')}{e.actorName ? ` · ${e.actorName}` : ''}</p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function notificationText(data: unknown): string {
  const d = data as { sent?: boolean; via?: string; reason?: string } | null;
  if (!d) return 'Notification';
  return d.sent ? `Customer notified (${d.via === 'template' ? 'template' : 'WhatsApp message'})` : `Customer not notified: ${d.reason}`;
}
